import "material-design-icons-iconfont/dist/material-design-icons.css";
import Vue from "vue";
import Vuetify from "vuetify/lib/framework";
import { readStoredThemeIsDark } from "@/utils/theme";
import FamilyHistoryIcon from "@/components/icons/FamilyHistoryIcon.vue";
import Graph2Icon from "../components/icons/Graph2Icon.vue";

Vue.use(Vuetify);

// Runtime colour palette. customProperties: true emits every key below as a
// --v-<key>-base custom property on :root; src/styles/variables.scss reads them
// via var(). Toggling theme.dark regenerates the :root block with the active
// theme's values, so one set of SCSS references drives both modes.
//
// textPrimary is ALSO defined as a literal hex in variables.scss (the
// $material-light/$material-dark maps): Vuetify 2 sets the default body-text
// colour only from those compile-time maps, while components read the same
// colour as --v-textPrimary-base. tests/unit/theme-sync.spec.js guards that the
// two copies stay equal. The app background comes solely from those Sass maps
// ($page-background-*), so it is not duplicated as a theme key here.
export default new Vuetify({
  // Register custom (non-font) icons as component icons. `iconfont` is left
  // unset so Vuetify's built-in icons keep using the default `mdi` preset;
  // this only ADDS values, referenced in templates as `$familyHistory`.
  icons: {
    values: {
      familyHistory: {
        component: FamilyHistoryIcon,
      },
      graph2: {
        component: Graph2Icon,
      },
    },
  },
  theme: {
    dark: readStoredThemeIsDark(),
    options: { customProperties: true },
    themes: {
      // Neutral (un-tinted) light elevation ladder, mirroring the dark ramp
      // below — each step is lighter than the last, so nested surfaces read as
      // progressively more raised in both modes. Three levels:
      //   page   #eaeaea — the app background behind everything (Sass map only)
      //   panel  #f5f5f5 — container surfaces: the dashboard cards, the app bar,
      //                    the leaderboard header, and every Vuetify list/menu
      //                    (it also sets the 'cards' Material role — Sass map)
      //   raised #ffffff — controls and tiles sitting ON a panel: filter inputs,
      //                    stat tiles, chips, the leaderboard toggle track and
      //                    its row hover/selected surface
      // The lime primary is the only colour; grey neutrals keep the surfaces
      // distinguishable without a green cast. textPrimary stays brand
      // dark-green — the deliberate light-mode text accent, matched to
      // $color-on-primary in variables.scss.
      light: {
        primary: "#DFF15A",
        background: "#eaeaea",
        panel: "#f5f5f5",
        raised: "#ffffff",
        textPrimary: "#163223",
        textMuted: "#838584",
        textFaint: "#8c8c8c",
        border: "#e6e6e6",
        divider: "#dbdbdb",
        // Pastel status colors (shared by light+dark). Light backgrounds, so
        // the snackbar that uses them pairs a dark text override — see App.vue.
        // success: "#86efac",
        // error: "#fca5a5",
        // warning: "#fcd34d",
        success: "#DFF15A",
        warning: "#f1a35a",
        error: "#f15a5a",
        info: "#cbd5e1",
      },
      // The same three levels in dark: page #121212 < panel #2a2a2a < raised
      // #333333, each carrying the role described above. `panel` is dual-homed —
      // it is also a literal hex in the Sass Material maps in variables.scss,
      // which is where the page background lives outright; the sync is guarded by
      // tests/unit/theme-sync.spec.js. The lime primary is the only colour; grey
      // neutrals keep the nested surfaces distinguishable without a green cast.
      dark: {
        primary: "#DFF15A",
        background: "#121212",
        panel: "#2a2a2a",
        raised: "#333333",
        textPrimary: "#e8e8e8",
        textMuted: "#a6a6a6",
        textFaint: "#7c7c7c",
        border: "#3a3a3a",
        divider: "#3d3d3d",
        // Same pastel status colors in dark mode; the snackbar keeps dark text
        // on them for legibility regardless of theme (see App.vue).
        // success: "#86efac",
        // error: "#fca5a5",
        // warning: "#fcd34d",
        success: "#DFF15A",
        warning: "#f1a35a",
        error: "#f15a5a",
        info: "#cbd5e1",
      },
    },
  },
});
